/* ============================================================
   hh-rotator.js — Homepage quote/fact rotator (Task 3)

   Reproduces Home.jsx QuoteMarquee (382-438): a discreet band below the hero
   that crossfades through one verified quote/fact at a time, driven by a
   SINGLE requestAnimationFrame clock that updates both the chambray progress
   bar (imperatively, via transform: scaleX) and the auto-advance, so they
   can't desync. Hover/focus pause the clock (smooth resume, not restart);
   honours prefers-reduced-motion (no autoplay, no bar); bare chevrons step
   manually and reset the bar.

   Theme-editor friendly: everything is scoped to the section root, re-inits on
   shopify:section:load, cancels its rAF on :unload, and multiple instances are
   safe (each carries its own state on the root via a WeakMap).
   ============================================================ */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-hh-rotator]';
  var instances = new WeakMap();

  var prefersReduce = !!(window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function destroy(root) {
    var state = instances.get(root);
    if (!state) return;
    if (state.raf) cancelAnimationFrame(state.raf);
    if (state.fadeTimer) clearTimeout(state.fadeTimer);
    if (state.cleanup) state.cleanup();
    instances.delete(root);
  }

  function init(sectionEl) {
    if (!sectionEl) return;
    var root = sectionEl.matches && sectionEl.matches(ROOT_SELECTOR)
      ? sectionEl
      : sectionEl.querySelector(ROOT_SELECTOR);
    if (!root) return; // no rotator in this section — no-op

    // Re-init cleanly (theme editor re-renders the section in place).
    destroy(root);

    var items = root.querySelectorAll('.hh-rotator__item');
    if (items.length === 0) return;

    var fill = root.querySelector('.hh-rotator__fill');
    var dwell = parseFloat(root.getAttribute('data-dwell')) || 6600; // ms an item rests
    var fade = parseFloat(root.getAttribute('data-fade')) || 440;    // ms crossfade
    var count = items.length;

    var state = {
      index: 0,
      shown: true,     // current item fully visible (fade not in progress)
      paused: false,
      progress: 0,     // 0..1 toward the next note
      lastTs: null,
      raf: null,
      fadeTimer: null,
      cleanup: null
    };
    instances.set(root, state);

    // Make sure exactly the first item is shown at start.
    for (var i = 0; i < count; i++) {
      items[i].classList.toggle('is-shown', i === 0);
    }
    state.index = 0;
    if (fill) fill.style.transform = 'scaleX(0)';

    // Advance by `dir` (+1 / -1). Every advance — auto or manual — resets the
    // bar. With reduced motion, swap instantly; otherwise fade out, then swap
    // .is-shown to the next item (modulo wrap) and fade in.
    function go(dir) {
      state.progress = 0;
      if (fill) fill.style.transform = 'scaleX(0)';

      if (prefersReduce) {
        items[state.index].classList.remove('is-shown');
        state.index = (state.index + dir + count) % count;
        items[state.index].classList.add('is-shown');
        return;
      }

      state.shown = false;
      items[state.index].classList.remove('is-shown');
      clearTimeout(state.fadeTimer);
      state.fadeTimer = setTimeout(function () {
        state.index = (state.index + dir + count) % count;
        items[state.index].classList.add('is-shown');
        state.shown = true;
      }, fade);
    }

    // ONE clock drives both the bar and the auto-advance: fill toward the next
    // note while the item rests (not paused, fade excluded), and advance at
    // 100%. Pausing just stops the clock, so on resume the bar continues
    // smoothly instead of restarting.
    function startClock() {
      if (prefersReduce) return;
      function tick(ts) {
        if (state.lastTs == null) state.lastTs = ts;
        var dt = ts - state.lastTs;
        state.lastTs = ts;
        if (!state.paused && state.shown) {
          state.progress = Math.min(1, state.progress + dt / dwell);
          if (fill) fill.style.transform = 'scaleX(' + state.progress + ')';
          if (state.progress >= 1) go(1);
        }
        state.raf = requestAnimationFrame(tick);
      }
      state.raf = requestAnimationFrame(tick);
    }

    // Pause on hover/focus; resume on leave/blur (smooth — the clock just stops).
    function pause() { state.paused = true; }
    function resume() { state.paused = false; state.lastTs = null; }

    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', resume);

    // Chevrons step manually and reset the bar.
    var chevHandlers = [];
    var chevs = root.querySelectorAll('.hh-rotator__chev');
    for (var c = 0; c < chevs.length; c++) {
      (function (btn) {
        var handler = function () {
          go(btn.getAttribute('data-dir') === 'left' ? -1 : 1);
        };
        btn.addEventListener('click', handler);
        chevHandlers.push([btn, handler]);
      })(chevs[c]);
    }

    state.cleanup = function () {
      root.removeEventListener('mouseenter', pause);
      root.removeEventListener('mouseleave', resume);
      root.removeEventListener('focusin', pause);
      root.removeEventListener('focusout', resume);
      for (var k = 0; k < chevHandlers.length; k++) {
        chevHandlers[k][0].removeEventListener('click', chevHandlers[k][1]);
      }
    };

    startClock();
  }

  function initAll(scope) {
    var ctx = scope || document;
    var roots = ctx.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target && e.target.matches && e.target.matches(ROOT_SELECTOR)
      ? e.target
      : (e.target && e.target.querySelector ? e.target.querySelector(ROOT_SELECTOR) : null);
    if (root) destroy(root);
  });
})();
